import { mergeById } from '@/lib/mergeById';

import { suspendedRowKey } from './flaggedSuspendedItems';
import type {
  DailyReportFileResult,
  DailyReportSummary,
  FlaggedPotfUpcRow,
  FlaggedRdStockRow,
  FlaggedSuspendedRow,
  FlaggedVendorItemListRow,
  FlaggedWebNoPriceRow,
} from './types';

function uniqueBy<T>(rows: readonly T[], keyOf: (row: T) => string): T[] {
  const seen = new Set<string>();
  const unique: T[] = [];
  for (const row of rows) {
    const key = keyOf(row);
    if (seen.has(key)) continue;
    seen.add(key);
    unique.push(row);
  }
  return unique;
}

function rdStockRowKey(row: FlaggedRdStockRow): string {
  return [row.company, row.division, row.stockLocation, row.productNumber].join(
    '\0',
  );
}

function webNoPriceRowKey(row: FlaggedWebNoPriceRow): string {
  return [row.div, row.productNumber].join('\0');
}

function potfUpcRowKey(row: FlaggedPotfUpcRow): string {
  return [row.sheetName, row.productLoc, row.productNumber].join('\0');
}

function vendorItemListRowKey(row: FlaggedVendorItemListRow): string {
  return [
    row.productNumber,
    row.englishDescription,
    row.frenchDescription,
  ].join('\0');
}

export function summarizeDailyReportResults(
  results: readonly DailyReportFileResult[],
): DailyReportSummary {
  const flagged = uniqueBy(results.flatMap(suspendedFlagged), suspendedRowKey);
  const rdStockMapped = uniqueBy(
    results.flatMap(kindRows('rdStockWithoutItem', 'mapped')),
    rdStockRowKey,
  );
  const rdStockUnmatched = uniqueBy(
    results.flatMap(kindRows('rdStockWithoutItem', 'flagged')),
    rdStockRowKey,
  );
  const supersededMapped = uniqueBy(
    results.flatMap(kindRows('supersededWithStock', 'mapped')),
    rdStockRowKey,
  );
  const supersededUnmatched = uniqueBy(
    results.flatMap(kindRows('supersededWithStock', 'flagged')),
    rdStockRowKey,
  );
  const webNoPrice = uniqueBy(
    results.flatMap(catalogFlagged('webNoPrice')),
    webNoPriceRowKey,
  );
  const potfUpc = uniqueBy(
    results.flatMap(catalogFlagged('potfUpc')),
    potfUpcRowKey,
  );
  const vendorItemList = uniqueBy(
    results.flatMap(vendorItemListFlagged),
    vendorItemListRowKey,
  );
  return {
    fileCount: results.length,
    analyzedCount: countStatus(results, 'analyzed'),
    recognizedCount: countStatus(results, 'recognized'),
    unrecognizedCount: countStatus(results, 'unrecognized'),
    unreadableCount: countStatus(results, 'unreadable'),
    rowsScanned: results.reduce(
      (total, result) =>
        total + (result.status === 'analyzed' ? result.rowsScanned : 0),
      0,
    ),
    flaggedCount:
      flagged.length +
      rdStockMapped.length +
      rdStockUnmatched.length +
      supersededMapped.length +
      supersededUnmatched.length +
      webNoPrice.length +
      potfUpc.length +
      vendorItemList.length,
    flagged,
    rdStockMappedCount: rdStockMapped.length,
    rdStockUnmatchedCount: rdStockUnmatched.length,
    rdStockMapped,
    rdStockUnmatched,
    supersededMappedCount: supersededMapped.length,
    supersededUnmatchedCount: supersededUnmatched.length,
    supersededMapped,
    supersededUnmatched,
    webNoPriceCount: webNoPrice.length,
    webNoPrice,
    potfUpcCount: potfUpc.length,
    potfUpc,
    vendorItemListCount: vendorItemList.length,
    vendorItemList,
  };
}

function suspendedFlagged(
  result: DailyReportFileResult,
): FlaggedSuspendedRow[] {
  return result.status === 'analyzed' && result.kind === 'suspendedItem'
    ? result.flagged
    : [];
}

function catalogFlagged(
  kind: 'webNoPrice',
): (result: DailyReportFileResult) => FlaggedWebNoPriceRow[];
function catalogFlagged(
  kind: 'potfUpc',
): (result: DailyReportFileResult) => FlaggedPotfUpcRow[];
function catalogFlagged(kind: 'webNoPrice' | 'potfUpc') {
  return (result: DailyReportFileResult) =>
    result.status === 'analyzed' && result.kind === kind ? result.flagged : [];
}

function vendorItemListFlagged(
  result: DailyReportFileResult,
): FlaggedVendorItemListRow[] {
  return result.status === 'analyzed' && result.kind === 'vendorItemList'
    ? result.flagged
    : [];
}

function kindRows(
  kind: 'rdStockWithoutItem' | 'supersededWithStock',
  field: 'mapped' | 'flagged',
): (result: DailyReportFileResult) => FlaggedRdStockRow[] {
  return (result) =>
    result.status === 'analyzed' && result.kind === kind ? result[field] : [];
}

function countStatus(
  results: readonly DailyReportFileResult[],
  status: DailyReportFileResult['status'],
): number {
  return results.filter((result) => result.status === status).length;
}

/** Later files with the same id replace earlier ones. */
export function mergeReportResults(
  current: readonly DailyReportFileResult[],
  incoming: readonly DailyReportFileResult[],
): DailyReportFileResult[] {
  return mergeById(current, incoming);
}
